import axios from 'axios';
import localApi from './localApi';

// Check storage mode from environment variable
// REACT_APP_STORAGE_MODE=local => use local IndexedDB
// otherwise => use cloud backend
const STORAGE_MODE = process.env.REACT_APP_STORAGE_MODE || 'local';
const IS_LOCAL_MODE = STORAGE_MODE === 'local';

// Log the mode for debugging (only in development)
if (process.env.NODE_ENV === 'development') {
  console.log(`[API] Storage mode: ${STORAGE_MODE}`);
}

// Session token storage key
const SESSION_TOKEN_KEY = 'armsroom.cloud.sessionToken';

/**
 * Get stored session token for Bearer authentication
 * Prefers sessionStorage (cleared on browser close) over localStorage
 */
export const getSessionToken = () => {
  return sessionStorage.getItem(SESSION_TOKEN_KEY) || localStorage.getItem(SESSION_TOKEN_KEY);
};

/**
 * Store session token for Bearer authentication
 * @param {string} token - The session token
 * @param {boolean} persistent - If true, store in localStorage (survives browser close)
 */
export const setSessionToken = (token, persistent = false) => {
  if (persistent) {
    localStorage.setItem(SESSION_TOKEN_KEY, token);
  } else {
    sessionStorage.setItem(SESSION_TOKEN_KEY, token);
  }
};

/**
 * Clear stored session token
 */
export const clearSessionToken = () => {
  sessionStorage.removeItem(SESSION_TOKEN_KEY);
  localStorage.removeItem(SESSION_TOKEN_KEY);
};

// Cloud API (axios-based)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const cloudApi = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  // Use withCredentials for cookie fallback, but prefer Bearer tokens
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - adds Authorization header with Bearer token
cloudApi.interceptors.request.use(
  (config) => {
    // Add Bearer token if available (preferred over cookies for security)
    const token = getSessionToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
cloudApi.interceptors.response.use(
  (response) => response,
  (error) => {
    // In local mode, NEVER redirect to login - there is no login
    // Local mode uses PIN lock instead
    if (IS_LOCAL_MODE) {
      return Promise.reject(error);
    }
    
    // Only in cloud mode: handle unauthorized by redirecting to login
    if (error.response?.status === 401) {
      // Clear any stale session tokens
      clearSessionToken();
      
      if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Export the appropriate API based on storage mode
const api = IS_LOCAL_MODE ? localApi : cloudApi;

// Export helper to check current mode
export const isLocalMode = () => IS_LOCAL_MODE;
export const getStorageMode = () => STORAGE_MODE;

export default api;
